import { Doc } from '@dadohr/shared/domainTypes/document';
import { EMPTY_ARR, EMPTY_OBJ } from '@dadohr/shared/domainTypes/emptyConstants';
import { IExperienceTemplate } from '@dadohr/shared/domainTypes/experience';
import { useTheme } from '@emotion/react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Link as MaterialUILink,
  MenuItem,
  Select,
  Typography
} from '@material-ui/core';
import { groupBy } from 'lodash';
import { Info } from 'lucide-react';
import { useMemo } from 'react';
import { AutocompleteSingle } from '../../../../../components/Autocomplete';
import { DialogTitleWithTypography } from '../../../../../components/DialogTitleWithTypography';
import { useDialogState } from '../../../../../hooks/useDialogState';
import { useUnsafeTranslation } from '../../../../../i18next';
import { FlexContainer } from '../../../../../layout/Flex';
import { useExperienceTemplateById } from '../../../../../services/experiences/template';
import { useActorDescriptorsFromTemplate } from '../../../../Experiences/components/Actors';

const SHOW_OTHER_TYPES = true; // not fully implemented yet

const useActorDescriptorsForActorsWithTasks = (
  tpl: Doc<IExperienceTemplate> | null
) => {
  const actorDescriptors = useActorDescriptorsFromTemplate(
    tpl?.data.supportingActors || EMPTY_OBJ
  );

  const tasks = tpl?.data.tasks || EMPTY_ARR;
  const tasksByActor = useMemo(() => groupBy(tasks, (tsk) => tsk.actor), [
    tasks
  ]);
  return useMemo(() => {
    return Object.fromEntries(
      Object.entries(actorDescriptors).filter(([k, v]) => {
        return tasksByActor[k]?.length;
      })
    );
  }, [actorDescriptors, tasksByActor]);
};

export const TypeSwitchForTemplate = ({
  tpl,
  type,
  setType,
  maxWidth,
  label
}: {
  tpl: Doc<IExperienceTemplate> | null;
  type: 'everyone' | string;
  setType: (nextType: 'everyone' | string) => void;
  maxWidth?: string | number;
  label?: string;
}) => {
  const { t } = useUnsafeTranslation();
  const actorDescriptors = useActorDescriptorsForActorsWithTasks(tpl || null);
  const options = useMemo<{ key: string; label: string }[]>(
    () => [
      {
        key: 'EVERYONE',
        label: t('Everyone in experience')
      },
      ...Object.values(actorDescriptors)
    ],
    [t, actorDescriptors]
  );
  const selectedOption = options.find((o) =>
    type === 'everyone' ? o.key === 'EVERYONE' : o.key === type
  );
  return (
    <AutocompleteSingle
      value={selectedOption || null}
      onChange={(nv) =>
        nv && setType(nv.key === 'EVERYONE' ? 'everyone' : nv.key)
      }
      maxWidth={maxWidth}
      disabledClearable
      label={label || t('Task Assignees')}
      getOptionLabel={(o) => o.label}
      options={options}
      fullWidth
    />
  );
};

export const TypeSwitch = ({
  tplIds,
  ...otherProps
}: {
  type: 'everyone' | string;
  setType: (nextType: 'everyone' | string) => void;
  tplIds: string[];
  maxWidth?: string | number;
  label?: string;
}) => {
  const [tpl] = useExperienceTemplateById(tplIds[0]);
  return <TypeSwitchForTemplate tpl={tpl || null} {...otherProps} />;
};

export const TypeHeading = ({
  type,
  tplIds
}: {
  type: 'everyone' | string;
  tplIds: string[];
}) => {
  const [tpl] = useExperienceTemplateById(tplIds[0]);
  const { t } = useUnsafeTranslation();
  const actorDescriptors = useActorDescriptorsForActorsWithTasks(tpl || null);
  const { dialogOpen, openDialog, closeDialog } = useDialogState();
  const label =
    type === 'everyone' ? t('all') : actorDescriptors[type]?.label || type;
  return (
    <>
      <FlexContainer direction="column" alignItems="start">
        <Typography variant="h3">
          {t('Completion for {{ type }} tasks', { type: label })}
        </Typography>

        <MaterialUILink style={{ cursor: 'pointer' }} onClick={openDialog}>
          <FlexContainer direction="row" alignItems="center">
            <Info size={20} />
            <Typography component="p" variant="body1">
              {t('What do the percentages mean?')}
            </Typography>
          </FlexContainer>
        </MaterialUILink>
      </FlexContainer>
      <Dialog open={dialogOpen} onClose={closeDialog}>
        <DialogTitleWithTypography>
          What do the percentages mean?
        </DialogTitleWithTypography>
        <DialogContent>
          <p>
            The percentages tell you how many of the tasks in this experience
            have been completed:
          </p>
          <ul>
            <li>100% means all tasks have been completed</li>
            <li>50% means half of the tasks have been completed</li>
            <li>0% means no tasks have been completed</li>
          </ul>
          <p>
            The color-coding indicates whether employees are falling behind:
          </p>
          <ul>
            <li>
              Green tells you that only one or two deadlines have been missed
            </li>
            <li>Yellow indicates a few deadlines have been missed</li>
            <li>Red indicates lots of deadlines have been missed</li>
            <li>Grey indicates that no task deadlines have passed yet</li>
          </ul>
        </DialogContent>
        <DialogActions>
          <Button variant="text" color="primary" onClick={closeDialog}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export const TypeSwitchHeadingStyle = ({
  type,
  setType,
  tplIds
}: {
  type: 'everyone' | string;
  setType: (nextType: 'everyone' | string) => void;
  tplIds: string[];
}) => {
  const { t } = useUnsafeTranslation();
  const theme = useTheme();

  const [tpl] = useExperienceTemplateById(tplIds[0]);
  const actorDescriptors = useActorDescriptorsForActorsWithTasks(tpl || null);
  const options = useMemo<{ key: string; label: string }[]>(
    () => [
      {
        key: 'EVERYONE',
        label: t('Completion for {{ type }} tasks', { type: t('all') })
      },
      ...Object.values(actorDescriptors).map((a) => ({
        key: a.key,
        label: t('Completion for {{ type }} tasks', { type: a.label })
      }))
    ],
    [t, actorDescriptors]
  );
  const selectedOption = options.find((o) =>
    type === 'everyone' ? o.key === 'EVERYONE' : o.key === type
  );

  if (!SHOW_OTHER_TYPES) {
    return (
      <Typography variant="h3">{t('Completion for all tasks')}</Typography>
    );
  }

  return (
    <Select
      value={selectedOption?.key || ''}
      variant="standard"
      style={{ ...theme.typography.h3, paddingRight: theme.spacing(1) }}
      onChange={(ev) => {
        const nv = ev.target.value as string;
        return nv && setType(nv === 'EVERYONE' ? 'everyone' : nv);
      }}
    >
      {options.map((o) => (
        <MenuItem key={o.key} value={o.key}>
          {o.label}
        </MenuItem>
      ))}
    </Select>
  );
};
